
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, User, Tag, Share2, Twitter, Linkedin, Facebook, Instagram, Shield, ArrowRight, ExternalLink } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { blogPosts } from '@/data/blogPosts.index';
import BlogCard from '@/components/blog/BlogCard';
import { getBlogHeroImage } from '@/lib/blogCategoryStyles';
import { isVisible } from '@/lib/draftPreview';
import { getSeriesForPostId, getPartNumber } from '@/data/series';
import SeriesCard from '@/components/series/SeriesCard';
import { getInternalLinkMeta } from '@/lib/internalLinkTypes';

const BlueskyIcon = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
     <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565-.139 2.038.07 3.711.487 5.722c.413 1.996 2.098 8.663 4.382 11.233.722.812 3.193 2.502 4.197 1.134.195-.266.702-1.343.702-1.343s.508 1.077.702 1.343c1.004 1.368 3.475-.322 4.197-1.134 2.284-2.57 3.969-9.237 4.382-11.233.417-2.011.626-3.684-.415-4.157-.659-.299-1.664-.621-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
  </svg>
);

const normalizeFrontmatterText = (value) => {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    return value.map(normalizeFrontmatterText).filter(Boolean).join(', ');
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 1) {
      const [k, v] = entries[0];
      const valueText = normalizeFrontmatterText(v);
      return valueText ? `${k}: ${valueText}` : k;
    }
    return entries
      .map(([k, v]) => {
        const valueText = normalizeFrontmatterText(v);
        return valueText ? `${k}: ${valueText}` : k;
      })
      .join(', ');
  }

  return '';
};

const blogSlugFromHref = (href) => typeof href === 'string'
  ? href.match(/^\/blog\/([^/?#]+)/)?.[1]
  : null;

/**
 * Production hosts never serve unpublished posts.
 *
 * `blogPosts` is the UNFILTERED registry — it contains every registered MDX file,
 * drafts included, because visibility is decided by `status` rather than by
 * registration. This page used to look a post up by slug with no status check, so
 * any draft was fully readable to anyone holding its URL (#363). Absence from the
 * sitemap is not access control.
 *
 * Drafts stay readable off production so beta remains a review surface — that is
 * how unpublished posts get read before they are published (#357). `DEV` covers
 * `npm run dev`; the hostname check covers beta.educ4te.com and *.pages.dev
 * preview builds, which are production builds and so not caught by `DEV`.
 */
// canPreviewDrafts / isVisible live in src/lib/draftPreview.js (shared with SeriesPage).

const BlogPost = ({ post }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const visibleBlogSlugs = useMemo(
    () => new Set(blogPosts.filter(isVisible).map((entry) => entry.slug)),
    []
  );
  const isSafeBlogHref = useCallback((href) => {
    const linkedSlug = blogSlugFromHref(href);
    return !linkedSlug || visibleBlogSlugs.has(linkedSlug);
  }, [visibleBlogSlugs]);
  const mdxComponents = useMemo(() => ({
    a: ({ href, children, ...props }) => isSafeBlogHref(href)
      ? <a href={href} {...props}>{children}</a>
      : <span {...props}>{children}</span>,
  }), [isSafeBlogHref]);

  const tldrPoints = Array.isArray(post.tldr)
    ? post.tldr.map(normalizeFrontmatterText).filter(Boolean)
    : [];

  const normalizedFaqs = Array.isArray(post.faqs)
    ? post.faqs
        .map((faq) => ({
          question: normalizeFrontmatterText(faq?.question),
          answer: normalizeFrontmatterText(faq?.answer),
        }))
        .filter((faq) => faq.question && faq.answer)
    : [];

  // Find related posts based on shared tags or semantic cluster
  const tags = Array.isArray(post.tags) ? post.tags : [];
  const relatedPosts = blogPosts
    // Status filter matters more here than on the direct lookup: without it a
    // published post could surface a draft as a "related" link, which is an active
    // discovery path rather than merely readable-if-you-know-the-URL (#363).
    .filter(p => p.id !== post.id && isVisible(p))
    .map(p => {
      const candidateTags = Array.isArray(p.tags) ? p.tags : [];
      const sharedTagCount = candidateTags.filter(tag => tags.includes(tag)).length;
      return {
        ...p,
        relevance: (p.semanticCluster === post.semanticCluster ? 10 : 0) + sharedTagCount
      };
    })
    .sort((a, b) => b.relevance - a.relevance)
    .slice(0, 3);
  
  // Series membership (#539). No-op for posts that are not in a registered series.
  const series = getSeriesForPostId(post.id);
  const seriesPart = series ? getPartNumber(series, post.id) : 0;
  const seriesNeighbour = (offset) => {
    if (!series) return null;
    const entry = series.parts[seriesPart - 1 + offset];
    const neighbour = entry && blogPosts.find((p) => p.id === entry.id && isVisible(p));
    return neighbour || null;
  };
  const seriesPrev = seriesNeighbour(-1);
  const seriesNext = seriesNeighbour(1);
  const seriesUrl = series ? `https://educ4te.com/series/${series.slug}` : null;

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const canonicalUrl = `https://educ4te.com/blog/${post.slug}`;
  const shareText = `Check out this article: ${post.title}`;

  // Sanitize blog content to prevent XSS attacks
  // Remove any leading decorative images (eg. default lion image) and strip known lion asset URLs
  // Note: MDX posts don't have post.content (they use MDXContent component instead)
  const cleanedContent = useMemo(() => {
    // Skip sanitization for MDX posts
    if (post.MDXContent) return '';

    let c = post.content || '';
    // Remove any <img> tags that reference the lion default host or filename
    c = c.replace(/<img[^>]*(?:i\.ibb\.co\/zWFjQbh2\/logo-mark-lion-dark\.[^"'<>\s]+)[^>]*>/gi, '');
    // Remove any leading <p><img ...></p> or leading <img ...> at the start of the content
    c = c.replace(/^\s*<p>\s*<img[^>]*>\s*<\/p>/i, '');
    c = c.replace(/^\s*<img[^>]*>/i, '');
    return c;
  }, [post.content, post.MDXContent]);

  // Configure DOMPurify to allow safe HTML elements and attributes
  // Note: Only used for legacy JS posts with HTML strings, not MDX posts
  const sanitizedContent = useMemo(() => {
    // Skip sanitization for MDX posts
    if (post.MDXContent) return '';

    return DOMPurify.sanitize(cleanedContent, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'strong', 'em', 'u', 'b', 'i',
        'a', 'img',
        'ul', 'ol', 'li',
        'blockquote', 'code', 'pre',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'div', 'span'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'rel',
        'src', 'alt', 'title', 'width', 'height',
        'class', 'id'
      ],
      ALLOW_DATA_ATTR: false,
      ADD_ATTR: ['target', 'rel']
    });
  }, [cleanedContent, post.MDXContent]);

  const toISOWithTZ = (d) => d && !d.includes('T') ? `${d}T00:00:00+10:00` : d;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription || post.excerpt,
    "inLanguage": "en-AU",
    "articleSection": post.category || "Cybersecurity",
    "image": post.image ? `https://educ4te.com${post.image}` : 'https://educ4te.com/images/educ4te.logos/educ4te.png',
    "datePublished": toISOWithTZ(post.date),
    "dateModified": toISOWithTZ(post.lastUpdated || post.date),
    "author": {
      "@type": "Person",
      "name": post.author,
      "url": "https://educ4te.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "@id": "https://educ4te.com/#organization",
      "name": "Educ4te",
      "logo": {
        "@type": "ImageObject",
        "url": "https://educ4te.com/images/educ4te.logos/educ4te.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    ...(series ? {
      "isPartOf": {
        "@type": "CreativeWorkSeries",
        "@id": `${seriesUrl}#series`,
        "name": series.title,
        "url": seriesUrl
      },
      "position": seriesPart
    } : {}),
    "keywords": (post.keywords || []).join(', ') || (Array.isArray(post.tags) ? post.tags.join(', ') : ''),
    "inLanguage": "en-AU",
    "articleSection": post.category,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".article-intro", ".tldr-section"]
    }
  };

  // BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://educ4te.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://educ4te.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": canonicalUrl
      }
    ]
  };

  // FAQ schema (if FAQs exist)
  const faqSchema = normalizedFaqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": normalizedFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // Dynamic CTA based on content intent
  const getCTAContent = () => {
    switch(post.intent) {
      case 'Transactional':
        return {
          title: 'Ready to Enrol?',
          description: `Take the next step in your ${post.category} journey with expert-led training.`,
          primaryText: post.cta?.primary || 'View Course Details',
          primaryLink: post.cta?.primaryLink || '/training',
          secondaryText: post.cta?.secondary || 'Explore All Courses',
          secondaryLink: post.cta?.secondaryLink || '/training'
        };
      
      case 'Commercial':
        return {
          title: 'Need Expert Guidance?',
          description: 'Book a consultation to assess your security posture and compliance needs.',
          primaryText: post.cta?.primary || 'Book Assessment',
          primaryLink: post.cta?.primaryLink || '/assessments',
          secondaryText: post.cta?.secondary || 'View All Assessments',
          secondaryLink: post.cta?.secondaryLink || '/assessments'
        };
      
      case 'Informational':
      default:
        return {
          title: 'Ready to Strengthen Your Security Posture?',
          description: 'Explore our assessments and training to build resilience across your Microsoft environment.',
          primaryText: post.cta?.primary || 'View Assessments',
          primaryLink: post.cta?.primaryLink || '/assessments',
          secondaryText: post.cta?.secondary || 'Explore Training',
          secondaryLink: post.cta?.secondaryLink || '/training'
        };
    }
  };

  const ctaContent = getCTAContent();

  return (
    <>
      <Helmet>
        {/* Must stay a SINGLE expression child. react-helmet-async passes <title>
            children straight to React 19's hoisting path, which silently renders
            an EMPTY <title> when children is an array (`{expr} | literal`). (#339) */}
        <title>{`${post.title} | Educ4te Blog`}</title>
        <meta name="description" content={post.metaDescription || post.excerpt} />
        <meta name="keywords" content={post.keywords?.join(', ') || post.tags.join(', ')} />
        <meta name="author" content={post.author} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="en_AU" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription || post.excerpt} />
        <meta property="og:image" content={`https://educ4te.com${getBlogHeroImage(post)}`} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.lastUpdated || post.date} />
        <meta property="article:author" content={post.author} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription || post.excerpt} />
        <meta name="twitter:image" content={`https://educ4te.com${getBlogHeroImage(post)}`} />
        
        {/* Schema Markup - Article */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        
        {/* Schema Markup - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        
        {/* Schema Markup - FAQ (if available) */}
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      <div className="pt-16 md:pt-20 pb-20 bg-white min-h-screen">
        {/* Breadcrumbs */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 md:mb-12">
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <Link to="/" className="hover:text-[#422680] transition-colors">Home</Link>
            <span>→</span>
            <Link to="/blog" className="hover:text-[#422680] transition-colors">Blog</Link>
            <span>→</span>
            <span className="text-gray-900 font-medium">{post.title.length > 50 ? post.title.substring(0, 50) + '...' : post.title}</span>
          </div>
        </div>

        {/* ===== HERO SECTION ===== */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 md:mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Category Pill */}
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 bg-[#422680]/10 text-[#422680] text-xs font-bold px-4 py-2 rounded-full border border-[#422680]/20">
                <Shield size={14} />
                {post.category}
              </span>
            </div>

{/* H1 Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-900">
              {post.title}
            </h1>

            {/* Summary (1-2 sentences) */}
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl">
              {post.excerpt}
            </p>

            {/* Meta Row */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 pb-8 border-b-2 border-gray-200">
              <div className="flex items-center gap-2">
                <User size={16} className="text-[#422680]" />
                <span className="font-medium">{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-[#422680]" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#422680]" />
                <span>{post.readTime}</span>
              </div>
              {post.australianContext && (
                <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-200 text-xs font-semibold">
                  🇦🇺 Australian Context
                </div>
              )}
              {post.complianceFrameworks && post.complianceFrameworks.length > 0 && (
                <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold">
                  📜 {post.complianceFrameworks.join(', ')}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* ===== KEY TAKEAWAYS BOX ===== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16"
        >
{tldrPoints.length > 0 && (
            <div className="bg-gradient-to-br from-[#422680]/5 to-[#F54952]/5 rounded-2xl p-8 border-2 border-[#422680]/20">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="text-2xl">⚡</span>
                TL;DR
              </h3>
              <ul className="space-y-3">
                {tldrPoints.map((point, index) => (
                  <li key={index} className="flex gap-4 text-gray-700 leading-relaxed">
                    <span className="text-[#422680] font-bold min-w-fit">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>

        {/* ===== TECHNICAL GUIDANCE DISCLAIMER (code / script posts) ===== */}
        {post.codeDisclaimer && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12"
          >
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 md:p-8">
              <h3 className="text-base md:text-lg font-bold text-amber-900 mb-3 flex items-center gap-2">
                <span className="text-xl">⚠️</span>
                Technical guidance — please read
              </h3>
              <p className="text-sm md:text-base text-amber-900/90 leading-relaxed">
                The PowerShell scripts and configuration samples in this article are
                provided as sample guidance only and were correct as at the date of
                publication{post.date ? ` (${post.date})` : (post.publishDate ? ` (${post.publishDate})` : '')}.
                Microsoft frequently updates its products, services, cmdlets and interfaces,
                so verify current behaviour against official Microsoft documentation before
                relying on anything here. Always test in a non-production environment before
                deploying to production. EDUC4TE provides this guidance "as is", without
                warranty of any kind, and accepts no liability for any loss, damage or
                disruption arising from its use.
              </p>
            </div>
          </motion.div>
        )}

        {/* ===== ARTICLE BODY ===== */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <motion.article
            data-post-ready="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="prose prose-lg prose-slate max-w-none mx-auto
                       prose-headings:font-bold prose-headings:text-gray-900
                       prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                       prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                       prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                       prose-a:text-[#422680] prose-a:no-underline hover:prose-a:underline
                       prose-strong:text-gray-900 prose-strong:font-bold
                       prose-ul:my-6 prose-li:my-2
                       prose-code:text-[#422680] prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded
                       prose-pre:bg-gray-900 prose-pre:text-gray-100"
          >
            {post.MDXContent ? (
              <post.MDXContent components={mdxComponents} />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
            )}
          </motion.article>
        </div>

        {/* ===== MID-ARTICLE CONTEXTUAL CTA (After Roadmap Section) ===== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 md:mb-20"
        >
          <div className="bg-white rounded-xl border-2 border-gray-200 hover:border-[#422680] transition-all duration-300 overflow-hidden">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              {/* Left Column: Assess Your Maturity */}
              <div className="p-6 md:p-8 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                    Assess Your Maturity
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 mb-6 leading-relaxed">
                    Understand where your organisation stands on the Essential Eight maturity model and identify priority controls.
                  </p>
                </div>
                <Link to="/assessments/essential-eight" className="block mt-4">
                  <Button size="lg" className="w-full bg-[#422680] hover:bg-[#660F56] text-white transition-all duration-300 font-semibold rounded-lg border-2 border-transparent">
                    Schedule Assessment
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
              
              {/* Right Column: Train Your Team */}
              <div className="p-6 md:p-8 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                    Train Your Team
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 mb-6 leading-relaxed">
                    Hands-on Essential Eight fundamentals course for security teams, architects, and IT leaders.
                  </p>
                </div>
                <Link to="/training/essential-eight-fundamentals" className="block mt-4">
                  <Button size="lg" className="w-full bg-[#422680] hover:bg-[#660F56] text-white transition-all duration-300 font-semibold rounded-lg border-2 border-transparent">
                    Explore Training
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

          {/* ===== RELATED RESOURCES CARDS ===== */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          {post.internalLinks && post.internalLinks.some((link) => isSafeBlogHref(link.href)) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                <span className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] bg-clip-text text-transparent">
                  Resources to Deepen Your Knowledge
                </span>
              </h3>
              <p className="text-gray-600 mb-8">Explore training, pathways, assessments and tools aligned with this article's content.</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {post.internalLinks.filter((link) => isSafeBlogHref(link.href)).map((link, index) => (
                  <Link key={index} to={link.href} className="group">
                    <div className="bg-white rounded-xl border-2 border-gray-200 hover:border-[#422680] p-6 h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="text-[#422680] text-xs uppercase font-bold tracking-wide mb-2">
                            {getInternalLinkMeta(link.type).label}
                          </div>
                          <h4 className="font-bold text-gray-900 text-lg group-hover:text-[#422680] transition-colors leading-snug">
                            {link.text}
                          </h4>
                        </div>
                        <ArrowRight size={20} className="text-[#422680] group-hover:translate-x-2 transition-transform flex-shrink-0" />
                      </div>
                      <p className="text-gray-600 text-sm">
                        {getInternalLinkMeta(link.type).blurb}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* ===== FAQ SECTION ===== */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          {normalizedFaqs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16 border-t-2 border-gray-200 pt-12"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                <span className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] bg-clip-text text-transparent">
                  Frequently Asked Questions
                </span>
              </h3>
              <p className="text-gray-600 mb-8">Questions security teams commonly ask about Essential Eight implementation.</p>
              
              <div className="space-y-4">
                {normalizedFaqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-[#422680] transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors group"
                    >
                      <h4 className="font-bold text-gray-900 text-lg group-hover:text-[#422680] transition-colors">
                        {faq.question}
                      </h4>
                      <span className={`text-[#422680] font-bold text-xl ml-4 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-45' : ''}`}>
                        +
                      </span>
                    </button>
                    {openFaqIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-6 pb-5 pt-0 text-gray-600 leading-relaxed border-t border-gray-100"
                      >
                        {faq.answer}
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* ===== STRONG BOTTOM CTA SECTION ===== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16"
        >
          <div className="bg-gradient-to-br from-[#422680] to-[#660F56] rounded-2xl p-8 md:p-12 text-white">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold mb-3">
                  Ready to Move from Level 1 to Level 2+?
                </h3>
                <p className="text-white/90 text-base md:text-lg leading-relaxed">
                  Start your 12-month Essential Eight maturity journey with expert guidance and practical roadmaps.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-shrink-0">
                <Link to="/assessments/essential-eight" className="w-full sm:w-auto">
                  <Button className="w-full bg-white text-[#422680] hover:bg-gray-100 hover:scale-105 transition-all duration-300 shadow-lg font-semibold">
                    Book Assessment
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/training/essential-eight-fundamentals" className="w-full sm:w-auto">
                  <Button className="w-full bg-white/20 border-2 border-white text-white hover:bg-white/30 hover:scale-105 transition-all duration-300 font-semibold">
                    Enroll in Training
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ===== TAGS & SHARE (CONSOLIDATED, SUBTLE) ===== */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="bg-gray-50 rounded-xl p-6 md:p-8 border border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              
              {/* Tags */}
              {Array.isArray(post.tags) && post.tags.length > 0 && (
                <div className="flex-1">
                  <div className="text-xs uppercase font-bold text-gray-600 mb-3 tracking-wide">Topics</div>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map(tag => (
                      <Link 
                        key={tag}
                        to={`/blog?tag=${tag}`}
                        className="bg-white text-[#422680] border border-gray-300 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-[#422680] hover:text-white hover:border-[#422680] transition-all"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Share Bar (Subtle) */}
              <div className="w-full md:w-auto border-t md:border-t-0 md:border-l border-gray-300 pt-6 md:pt-0 md:pl-8">
                <div className="text-xs uppercase font-bold text-gray-600 mb-3 tracking-wide">Share</div>
                <div className="flex gap-2">
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn">
                    <Button size="icon" variant="outline" className="rounded-lg h-9 w-9 hover:text-[#0A66C2] hover:border-[#0A66C2] hover:bg-[#0A66C2]/5 transition-all">
                      <Linkedin size={16} />
                    </Button>
                  </a>
                  <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(currentUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter">
                    <Button size="icon" variant="outline" className="rounded-lg h-9 w-9 hover:text-[#1DA1F2] hover:border-[#1DA1F2] hover:bg-[#1DA1F2]/5 transition-all">
                      <Twitter size={16} />
                    </Button>
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
                    <Button size="icon" variant="outline" className="rounded-lg h-9 w-9 hover:text-[#1877F2] hover:border-[#1877F2] hover:bg-[#1877F2]/5 transition-all">
                      <Facebook size={16} />
                    </Button>
                  </a>
                  <a href={`https://bsky.app/intent/compose?text=${encodeURIComponent(shareText + ' ' + currentUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Bluesky">
                    <Button size="icon" variant="outline" className="rounded-lg h-9 w-9 hover:text-[#0085ff] hover:border-[#0085ff] hover:bg-[#0085ff]/5 transition-all">
                      <BlueskyIcon size={16} />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Series navigation (#539) */}
        {series && (
          <nav aria-label={`${series.shortTitle} series`} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
            <div className="rounded-xl border border-[#422680]/30 bg-[#422680]/5 p-5">
              <div className="mb-4 max-w-sm">
                <SeriesCard series={series} compact headingLevel="h2" />
              </div>
              <p className="text-sm font-semibold text-[#422680] mb-1">
                Part {seriesPart} of {series.totalParts} in the series
              </p>
              <p className="font-bold text-gray-900 mb-3">
                <Link to={`/series/${series.slug}`} className="hover:text-[#422680] underline">{series.shortTitle}</Link>
              </p>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-3 text-sm">
                {seriesPrev ? (
                  <Link to={`/blog/${seriesPrev.slug}`} className="text-[#422680] font-medium hover:underline">
                    Previous: Part {seriesPart - 1}, {seriesPrev.title}
                  </Link>
                ) : <span />}
                {seriesNext && (
                  <Link to={`/blog/${seriesNext.slug}`} className="text-[#422680] font-medium hover:underline sm:text-right">
                    Next: Part {seriesPart + 1}, {seriesNext.title}
                  </Link>
                )}
              </div>
            </div>
          </nav>
        )}

        {/* Back to Blog */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 mt-12">
          <Link to="/blog">
            <Button variant="outline" className="group hover:border-[#422680] hover:text-[#422680] transition-all">
              <ArrowLeft size={18} className="mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to All Articles
            </Button>
          </Link>
        </div>

        {/* ===== READ NEXT / RELATED POSTS ===== */}
        {relatedPosts.length > 0 && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 border-t-2 border-gray-200 pt-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-2">
                <span className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] bg-clip-text text-transparent">
                  Read Next
                </span>
              </h3>
              <p className="text-gray-600 mb-8">Continue exploring security insights and best practices</p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-20">
                {relatedPosts.map((related, index) => (
                  <BlogCard key={related.id} post={related} variant="related" index={index} />
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlogPost;
